import { PageHero } from "@/app/common/components/composite/layout/PageHero";
import { PageSection } from "@/app/common/components/composite/layout/PageSection";
import { OfferingList } from "@/app/common/components/composite/list/OfferingList";
import { QuestionList } from "./QuestionList";
import { RelatedLinkList } from "@/app/common/components/composite/navigation/RelatedLinkList";
import { ClosingBand } from "@/app/common/components/composite/layout/ClosingBand";
import {
  getCapabilityBySlug,
  type CapabilityRecord,
} from "@/content/capabilities";
import { ServiceSubsection } from "@/app/features/services/components/ServiceSubsection";
import { getIndustryBySlug } from "@/content/industries";
import { getServicesByCapability } from "@/content/services";
import { routes } from "@/app/common/functions/routes/routes";
import { PageArticle } from "@generalapplications/ui";

export type CapabilityTemplateProps = {
  capability: CapabilityRecord;
};

export function CapabilityTemplate({ capability }: CapabilityTemplateProps) {
  const namedServices = getServicesByCapability(capability.slug);

  const relatedItems = [
    ...capability.relatedCapabilities.flatMap((slug) => {
      const related = getCapabilityBySlug(slug);
      return related
        ? [{ label: related.title, href: routes.capabilities.detail(slug) }]
        : [];
    }),
    ...capability.relatedIndustries.flatMap((slug) => {
      const related = getIndustryBySlug(slug);
      return related
        ? [{ label: related.title, href: routes.industries.detail(slug) }]
        : [];
    }),
  ];

  return (
    <PageArticle rhythm="xl">
      {/* No eyebrow: the breadcrumb above already reads "Capabilities /
          Intelligent Systems", and one orientation label per page is the rule.
          Index pages keep their badge because Breadcrumbs suppresses a
          single-crumb trail, so there the badge is the only label there is. */}
      <PageHero
        headline={capability.headline}
        copy={capability.heroCopy}
        image={capability.image}
      />

      <PageSection title="What We Build">
        <OfferingList items={capability.offerings} ariaLabel="What we build" />

        <ServiceSubsection
          services={namedServices}
          copy="Specific, scoped work within this discipline. Each service page states how that work is grounded."
          ariaLabel="Services in this capability"
        />
      </PageSection>

      <PageSection
        title={capability.principle.headline}
        body={capability.principle.copy}
      />

      <PageSection title="Typical Questions">
        <QuestionList
          items={capability.typicalQuestions}
          ariaLabel="Typical questions"
        />
      </PageSection>

      {relatedItems.length > 0 && (
        <PageSection title="Related">
          <RelatedLinkList
            items={relatedItems}
            ariaLabel="Related capabilities and industries"
          />
        </PageSection>
      )}

      <ClosingBand
        headline="Have a system that touches this capability?"
        copy={[
          "Bring the workflow, the data problem, or the AI question — we will help make the next move clearer.",
        ]}
        cta="Discuss a System"
        ctaHref={routes.contact}
      />
    </PageArticle>
  );
}
